import React from 'react';
import CventBlockThemeProvider, { BlockThemeProviderProps } from '@cvent/blocks/components/BlockThemeProvider';
import { ThemeColors } from '@cvent/blocks/utils/types';
import { useThemeValues } from '@context/ThemeProvider';

export type { BlockThemeProviderProps };

type Props = React.PropsWithChildren<Omit<BlockThemeProviderProps, keyof ThemeColors>>;

/**
 * BlockThemeProvider already hooked up to theme colours.
 * Relies on being in a ThemeProvider context.
 */
export default function BlockThemeProvider(props: Props): JSX.Element {
  const { primary, secondary, background, mood, safeMode, preferFormula, headingFont, bodyFont } = useThemeValues();

  return (
    <CventBlockThemeProvider
      primary={primary}
      secondary={secondary}
      background={background}
      mood={mood}
      safeMode={safeMode}
      preferFormula={preferFormula}
      headingFont={headingFont}
      bodyFont={bodyFont}
      {...props}
    />
  );
}

/**
 * Helper component that returns a BlockThemeProvider with blockColorKey="mood" by default
 */
export function MoodThemeProvider(props: Props) {
  return <BlockThemeProvider blockColorKey="mood" {...props} />;
}

/**
 * Helper component that returns a BlockThemeProvider with blockColorKey="primary" by default
 */
export function PrimaryThemeProvider(props: Props) {
  return <BlockThemeProvider blockColorKey="primary" {...props} />;
}

/**
 * Helper component that returns a BlockThemeProvider with blockColorKey="background" by default
 */
export function BackgroundThemeProvider(props: Props) {
  return <BlockThemeProvider blockColorKey="background" {...props} />;
}

/**
 * Wraps the component in a mood theme provider
 */
export function withMoodThemeProvider<T>(WrappedComponent: React.ComponentType<T>): {
  (props: T): JSX.Element;
} {
  function ComponentWithMoodProvider(props: T): JSX.Element {
    return (
      <MoodThemeProvider>
        <WrappedComponent {...props} />
      </MoodThemeProvider>
    );
  }
  return ComponentWithMoodProvider;
}
